import React from 'react';
import {
  Box, Typography, Paper, Grid, Divider, Card, CardContent, Dialog, DialogTitle, DialogContent, IconButton,
  useTheme, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDatewithMonth } from '../../../helpers/handleDate';
import { color } from '@mui/system';
const ViewLead = ({ lead, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!lead) return null;

  const formatPhone = (phone) => {
    if (!phone || phone.length < 10) return phone || '—';
    return `${phone.slice(0, 5)} ${phone.slice(5)}`;
  };

  const InfoRow = ({ label, value, isDate }) => (
    <Box mb={3}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="medium" color={isDate ? "purple" : "inherit"}>
        {value || '—'}
      </Typography>
    </Box>
  );

  const RemarkCard = ({ remark }) => (
    <Box mb={2}>
      <Typography variant="body1" textAlign="center" mb={1} fontWeight="normal" color='purple'>
        {formatDatewithMonth(remark.time)}
      </Typography>
      <Card
        elevation={0}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          bgcolor: '#ffffff',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            mb={1}
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? 1 : 0}
          >
            <Typography variant="body2" color="purple">
              Follow-up On: <span style={{color: 'black'}}>{formatDatewithMonth(remark.follow_up_on) || '—'}</span>
            </Typography>
            <Typography variant="body2" color="purple">
              Followed Up On: <span style={{color: 'black'}}>{formatDatewithMonth(remark.followed_up_on) || '—'}</span>
            </Typography>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Typography variant="subtitle1" component="span" fontWeight="bold" sx={{ color: 'grey' }}>
              {remark.name}:
            </Typography>
            <Typography variant="subtitle1" component="span" sx={{ fontSize: '1.3rem', ml: 1 }}>
              {remark.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  let followUpRemarks = [];
  try {
    followUpRemarks = lead.follow_up ? JSON.parse(lead.follow_up) : [];
    if (!Array.isArray(followUpRemarks)) {
      followUpRemarks = [];
    }
  } catch (error) {
    console.error('Error parsing follow_up JSON:', error);
    followUpRemarks = [];
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#f8f8f8",
          color: "#481E82",
          fontWeight: "500",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        View Lead: {lead.name}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 2, paddingBottom: 2, backgroundColor: '#F1F4F9' }}>
        <Grid container spacing={3} sx={{marginTop: 2, marginBottom: 2}}>
          {/* Lead Details Section */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={1}
              sx={{
                p: 4,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                height: '100%',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Name" value={lead.name} />
                  <InfoRow label="Email" value={lead.email} />
                  <InfoRow label="Hometown" value={lead.hometown} />
                  <InfoRow label="Department" value={lead.department} />
                  <InfoRow label="Channel" value={lead.channel} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Phone" value={formatPhone(lead.phone)} />
                  <InfoRow label="Pass-out Year" value={lead.pass_out_year} />
                  <InfoRow
                    label="Next Follow-Up Date"
                    value={formatDatewithMonth(lead.next_follow_up_date)}
                    isDate
                  />
                  <InfoRow label="Course" value={lead.course} />
                  <InfoRow label="Status" value={lead.status} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {/* Remarks Timeline Section */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                bgcolor: 'rgb(200, 236, 250)',
                border: '2px solid #b0d4e8',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                color="#481E82"
                mb={2}
                pl={1}
              >
                Remarks
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box
                sx={{
                  overflowY: 'auto',
                  flexGrow: 1,
                  maxHeight: '400px',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.3)',
                    },
                  },
                }}
              >
                {followUpRemarks.length > 0 ? (
                  followUpRemarks.map((remark, index) => (
                    <RemarkCard key={index} remark={remark} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No remarks available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ViewLead;